/**
 * Professionelle ECharts-Defaults für die ganze App.
 * Wird in jedem Chart als `theme="stat"` referenziert (oder direkt in den Optionen gemerged).
 */

import * as echarts from 'echarts/core';

export const COLORS = {
  primary: '#1f97f5',
  primaryLight: '#87cfff',
  primarySoft: '#d8efff',
  vorjahr: '#94a3b8', // slate-400
  positive: '#12b76a',
  negative: '#f97066',
  warning: '#f79009',
  axis: '#94a3b8',
  axisLight: '#cbd5e1',
  text: '#475569',
  textMuted: '#94a3b8',
  bg: '#ffffff',
  gridLine: 'rgba(148, 163, 184, 0.15)',
  // Dark
  darkText: '#cbd5e1',
  darkTextMuted: '#64748b',
  darkAxis: '#475569',
  darkGridLine: 'rgba(148, 163, 184, 0.1)',
} as const;

/** Sequenz für Multi-Series-Charts. */
export const SERIES_PALETTE = [
  '#1f97f5',
  '#12b76a',
  '#f79009',
  '#f97066',
  '#8b5cf6',
  '#06b6d4',
  '#ec4899',
  '#84cc16',
  '#f43f5e',
  '#0ea5e9',
];

function isDark(): boolean {
  if (typeof document === 'undefined') return false;
  return document.documentElement.classList.contains('dark');
}

/** Basis-Tooltip-Style, dezent mit dünnen Borders. */
export function tooltipStyle() {
  const dark = isDark();
  return {
    backgroundColor: dark ? '#1e293b' : '#ffffff',
    borderColor: dark ? '#334155' : '#e2e8f0',
    borderWidth: 1,
    textStyle: {
      color: dark ? '#e2e8f0' : '#0f172a',
      fontSize: 12,
      fontFamily: 'Inter, sans-serif',
    },
    padding: [8, 12],
    extraCssText: `box-shadow: 0 8px 24px -4px rgba(15,23,42,${dark ? '0.6' : '0.12'}); border-radius: 8px;`,
  };
}

/** Standard-Achsen-Style. */
export function axisStyle() {
  const dark = isDark();
  return {
    axisLine: {
      lineStyle: { color: dark ? COLORS.darkAxis : COLORS.axisLight },
    },
    axisTick: { show: false },
    axisLabel: {
      color: dark ? COLORS.darkTextMuted : COLORS.textMuted,
      fontSize: 10.5,
      // Mono = Mess-Beschriftung: Achsen sprechen die gleiche Sprache wie
      // die KPI-Labels (JetBrains Mono, tabellarische Ziffern).
      fontFamily: "'JetBrains Mono', ui-monospace, monospace",
    },
    splitLine: {
      lineStyle: { color: dark ? COLORS.darkGridLine : COLORS.gridLine, type: 'dashed' as const },
    },
  };
}

export function baseOption() {
  return {
    textStyle: { fontFamily: 'Inter, sans-serif' },
    tooltip: tooltipStyle(),
    color: SERIES_PALETTE,
    animationDuration: 250,
    animationEasing: 'cubicOut' as const,
  };
}

// Stelle die echarts-Instanz an einer globalen Stelle bereit (nicht zwingend nötig, aber für Registrierung)
export { echarts };
